/**
 * AccountingStartDateSetup - Critical Onboarding & Settings Component
 * 
 * This component handles:
 * 1. Initial setup - Users MUST set a start date before any transactions
 * 2. Change flow - Users can change the date (with warnings about consequences)
 * 
 * Changing the date has serious implications:
 * - Moving FORWARD: Transactions in the affected period are DELETED
 * - Moving BACKWARD: User must re-upload bank feeds to capture older transactions
 */

import React, { useState, useEffect } from 'react';
import { StartDateImpactReview } from '../banking/GlobalStartDateDecisionProvider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Badge } from '../ui/badge';
import { format, subDays, startOfMonth, startOfYear, subMonths, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Calendar, CalendarDays, CheckCircle2, AlertTriangle, Info, ArrowRight,
  Building2, Landmark, FileText, Clock, Shield, HelpCircle, Sparkles,
  ChevronRight, Lock, Ban, CircleDollarSign, BookOpen, Edit, ArrowDown,
  ArrowUp, Trash2, RefreshCw, AlertOctagon, Download, XCircle, Loader2, Upload
} from 'lucide-react';
import BacklogFeeDialog from '../BacklogFeeDialog';
import { isIOSNative } from '../../utils/nativeBridge';

const AccountingStartDateSetup = ({ onComplete, embedded = false, allowChange = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { business, refreshBusiness } = useAuth();
  
  // Determine if we're in "change" mode
  const isChangeMode = searchParams.get('change') === 'true' || allowChange;
  const fromBacklogDecline = searchParams.get('from') === 'backlog-decline';
  
  // State
  const [startDate, setStartDate] = useState('');
  const [confirmUnderstanding, setConfirmUnderstanding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showChangeMode, setShowChangeMode] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [fiscalYearStart, setFiscalYearStart] = useState(null);
  
  // Confirmation dialog states
  const [showForwardWarning, setShowForwardWarning] = useState(false);
  const [showBackwardWarning, setShowBackwardWarning] = useState(false);
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [confirmOpeningBalanceDeletion, setConfirmOpeningBalanceDeletion] = useState(false);
  const [confirmFetchNeeded, setConfirmFetchNeeded] = useState(false);
  
  // Start date lock + backlog fee states
  const [lockStatus, setLockStatus] = useState(null);
  const [showBacklogFee, setShowBacklogFee] = useState(false);
  const [backlogFeeInfo, setBacklogFeeInfo] = useState(null);
  // Jun 2026 - backlog handling choice when the new date creates a backlog
  const [backlogChoice, setBacklogChoice] = useState(null);
  
  // Fetch lock status + fiscal year start
  useEffect(() => {
    const country = business?.country;
    if (country) {
      axios.get(`${API}/fiscal-year-start/${country}`)
        .then(res => setFiscalYearStart(res.data.fiscal_year_start))
        .catch(() => setFiscalYearStart(`${new Date().getFullYear()}-01-01`));
    }
    // Fetch lock status
    axios.get(`${API}/billing/start-date-lock-status`)
      .then(res => setLockStatus(res.data))
      .catch(() => {});
  }, [business?.country]);
  
  // Quick date options - month-based (always 1st of month)
  const today = new Date();
  const startOfCurrentMonth = format(startOfMonth(today), 'yyyy-MM-dd');
  const startOfLastMonth = format(startOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
  const quickDateOptions = [
    ...(fiscalYearStart ? [{
      label: 'Start of Tax Year',
      date: fiscalYearStart,
      description: `Begin from ${format(new Date(fiscalYearStart), 'MMMM yyyy')}`
    }] : []),
    { 
      label: 'This Month', 
      date: startOfCurrentMonth,
      description: `${format(startOfMonth(today), 'MMMM yyyy')}. Current month is free`
    },
    { 
      label: 'Last Month', 
      date: startOfLastMonth,
      description: `${format(startOfMonth(subMonths(today, 1)), 'MMMM yyyy')}. Backlog fee may apply`
    },
  ];
  
  // Helper: format a YYYY-MM-DD to "Month YYYY"
  const formatMonthYear = (dateStr) => {
    if (!dateStr) return '';
    try { return format(new Date(dateStr), 'MMMM yyyy'); } catch { return dateStr; }
  };
  
  // Calculate opening balance date (last day of month BEFORE start month)
  const openingBalanceDate = startDate 
    ? format(subDays(new Date(startDate), 1), 'yyyy-MM-dd')
    : null;

  // Fetch preview when date changes in change mode
  useEffect(() => {
    if ((showChangeMode || isChangeMode) && startDate && startDate !== business?.accounting_start_date) {
      fetchPreview(startDate);
    } else {
      setPreview(null);
    }
  }, [startDate, showChangeMode, isChangeMode]);

  const fetchPreview = async (newDate) => {
    setLoadingPreview(true);
    try {
      const response = await axios.get(`${API}/business/start-date-change-preview?new_date=${newDate}`);
      setPreview(response.data);
      if (response.data?.creates_backlog) {
        setBacklogChoice(prev => prev || (isIOSNative() ? 'free' : (response.data.backlog_ai_choice || 'free')));
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast.error('Failed to preview changes');
    } finally {
      setLoadingPreview(false);
    }
  };
  
  // Check if selected date triggers a backlog fee
  const checkBacklogFee = async (dateStr) => {
    try {
      const res = await axios.post(`${API}/billing/calculate-backlog-fee`, { start_date: dateStr });
      if (res.data.is_backlog && res.data.card_required) {
        setBacklogFeeInfo({ ...res.data, start_date: dateStr });
        setShowBacklogFee(true);
        return true; // has backlog
      }
    } catch (err) {
      console.error('Backlog fee check failed:', err);
    }
    return false; // no backlog
  };

  // Handle initial setup (first time setting the date)
  const handleInitialSubmit = async () => {
    if (!startDate) {
      toast.error('Please select a start date');
      return;
    }
    
    if (!confirmUnderstanding) {
      toast.error('Please confirm you understand the implications');
      return;
    }
    
    // Check for backlog fee before proceeding
    const hasBacklog = await checkBacklogFee(startDate);
    if (hasBacklog) return; // Dialog will handle the flow
    
    await doActivatePlatform();
  };

  // Actually activate the platform (called after backlog fee is acknowledged or not needed)
  const doActivatePlatform = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/business/accounting-start-date`, {
        accounting_start_date: startDate,
        confirm_understanding: true
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Trigger historical bank data sync if open banking is connected
        try {
          await axios.post(`${API}/open-banking-feeds/trigger-historical-sync`);
        } catch (syncErr) {
          // Non-blocking: sync trigger is best-effort
          console.log('Historical sync trigger (non-blocking):', syncErr?.message);
        }
        
        // Refresh business context
        if (refreshBusiness) {
          await refreshBusiness();
        }
        
        // Navigate or callback
        if (onComplete) {
          onComplete(response.data);
        } else if (fromBacklogDecline) {
          toast.success('Start date updated! Now link your bank account.');
          navigate('/settings?tab=banking');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Error setting start date:', error);
      toast.error(error.response?.data?.detail || 'Failed to set start date');
    } finally {
      setLoading(false);
    }
  };

  // Handle changing the start date (destructive operation)
  const handleChangeSubmit = async () => {
    if (!preview) {
      toast.error('Please wait for the preview to load');
      return;
    }

    // If moving forward and there are affected transactions
    if (preview.direction === 'forward' && preview.total_affected > 0) {
      if (!confirmDeletion) {
        setShowForwardWarning(true);
        return;
      }
    }

    // If there are opening balances
    if (preview.direction !== 'backward' && preview.has_opening_balances && !confirmOpeningBalanceDeletion) {
      toast.error('You must delete existing opening balances first');
      return;
    }

    if (preview.direction === 'forward' && preview.forward_blocked) {
      toast.error(preview.forward_block_reason || 'Forward moves are only possible while the books are empty.');
      return;
    }
    // If moving backward
    if (preview.direction === 'backward') {
      if (!confirmFetchNeeded) {
        setShowBackwardWarning(true);
        return;
      }
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/business/change-accounting-start-date`, {
        new_accounting_start_date: startDate,
        confirm_opening_balance_deletion: confirmOpeningBalanceDeletion || !preview.has_opening_balances || (preview.direction === 'backward' && confirmFetchNeeded),
        confirm_transaction_handling: true,
        force_delete_transactions: confirmDeletion,
        ...(preview?.creates_backlog
          ? { backlog_ai_choice: isIOSNative() ? 'free' : (backlogChoice || 'free') }
          : {})
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Trigger historical bank data sync for the new date range
        try {
          await axios.post(`${API}/open-banking-feeds/trigger-historical-sync`);
        } catch (syncErr) {
          console.log('Historical sync trigger (non-blocking):', syncErr?.message);
        }
        
        // Refresh business context
        if (refreshBusiness) {
          await refreshBusiness();
        }
        
        // Show next steps
        if (response.data.next_steps && response.data.next_steps.length > 0) {
          toast.info(response.data.next_steps[0]);
        }
        
        // Navigate back to settings or dashboard
        if (onComplete) {
          onComplete(response.data);
        } else if (fromBacklogDecline) {
          toast.success('Start date updated! Now link your bank account.');
          navigate('/settings?tab=banking');
        } else {
          navigate('/settings?tab=general');
        }
      } else if (response.data.requires_confirmation) {
        // Handle different confirmation types
        if (response.data.confirmation_type === 'opening_balance_deletion') {
          toast.error('Please delete opening balances first');
        } else if (response.data.confirmation_type === 'transaction_deletion') {
          setShowForwardWarning(true);
        } else if (response.data.confirmation_type === 'backward_extension') {
          setShowBackwardWarning(true);
        }
      }
    } catch (error) {
      console.error('Error changing start date:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'object' ? (detail?.message || 'Failed to change start date') : (detail || 'Failed to change start date'));
    } finally {
      setLoading(false);
    }
  };

  // Render for already activated platform (with option to change)
  if (business?.platform_activated && !showChangeMode && !isChangeMode) {
    return (
      <div className={embedded ? "" : "min-h-dvh bg-gradient-to-b from-slate-50 to-slate-100 p-4"}>
        <div className={embedded ? "" : "max-w-2xl mx-auto pt-8"}>
          
          {/* Current Status Card */}
          <Card className="shadow-lg">
            <CardHeader className="border-b bg-green-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-green-900">Financial Capture Active</CardTitle>
                    <CardDescription className="text-green-700">
                      We've been automatically capturing your financial data since {formatMonthYear(business.accounting_start_date)}
                    </CardDescription>
                  </div>
                </div>
                <Badge className="bg-green-600">Active</Badge>
              </div>
            </CardHeader>
            
            <CardContent className="p-6 space-y-6">
              {/* Current Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarDays className="w-4 h-4 text-[#108981]" />
                    <span className="text-sm font-medium text-slate-600">Auto-Capture Start Month</span>
                  </div>
                  <p className="text-xl font-mono font-bold text-slate-900">{formatMonthYear(business.accounting_start_date)}</p>
                  <p className="text-xs text-slate-500 mt-1">Financial data captured automatically from this month</p>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-slate-600">Opening Balance Date</span>
                  </div>
                  <p className="text-xl font-mono font-bold text-slate-900">
                    {formatMonthYear(business.opening_balance_date || 
                      format(subDays(new Date(business.accounting_start_date), 1), 'yyyy-MM-dd'))}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">The day before auto-capture begins: your financial starting point</p>
                </div>
              </div>

              {/* Info about what this means */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">What this means</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>We automatically capture all transactions from <strong>{formatMonthYear(business.accounting_start_date)}</strong> onwards</li>
                      <li>Bank feeds, invoices, and bills are tracked from this month</li>
                      <li>Opening balances (your starting point) are set as of the end of: <strong>{formatMonthYear(business.opening_balance_date || format(subDays(new Date(business.accounting_start_date), 1), 'yyyy-MM-dd'))}</strong></li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Change Date Option */}
              {lockStatus?.is_locked ? (
                <div className="p-4 bg-slate-100 rounded-lg border">
                  <div className="flex gap-3">
                    <Lock className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-slate-700 mb-1">Start Date Locked</h4>
                      <p className="text-sm text-slate-600">
                        To protect your financial data integrity, the accounting start date is locked after 30 days of usage. 
                        For complex migrations, please contact support.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-amber-900 mb-1">Need to change this month?</h4>
                      <p className="text-sm text-amber-800 mb-1">
                        Changing your auto-capture start month is a <strong>serious operation</strong> that may 
                        result in data being deleted. Only proceed if you understand the consequences.
                      </p>
                      <p className="text-xs text-muted-foreground mb-3">
                        You can move your start month as far back as your records go. If a statement you upload reaches further back, we will offer to move it for you.
                      </p>
                      <Button 
                        variant="outline"
                        className="border-amber-400 text-amber-800 hover:bg-amber-100"
                        onClick={() => {
                          setShowChangeMode(true);
                          setStartDate(business.accounting_start_date);
                        }}
                        data-testid="change-start-date-btn"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Change Start Month
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
            
            <CardFooter className="border-t bg-slate-50 p-4">
              <div className="w-full flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/settings?tab=general')}
                  className="flex-1"
                >
                  Back to Settings
                </Button>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 bg-[#108981] hover:bg-[#108981]/90"
                >
                  Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Render the change mode interface
  if (showChangeMode || isChangeMode) {
    return (
      <div className={embedded ? "" : "min-h-dvh bg-gradient-to-b from-slate-50 to-slate-100 p-4"}>
        <div className={embedded ? "" : "max-w-2xl mx-auto pt-8"}>
          
          {/* Header */}
          {!embedded && (
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                <AlertOctagon className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Change Auto-Capture Start Month</h1>
              <p className="text-slate-600 mt-2">
                Current auto-capture month: <strong>{formatMonthYear(business?.accounting_start_date)}</strong>
              </p>
            </div>
          )}

          {/* Warning Banner */}
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertOctagon className="h-5 w-5 text-red-600" />
            <AlertTitle className="text-red-800">Destructive Operation</AlertTitle>
            <AlertDescription className="text-red-700">
              Changing your auto-capture start date may result in <strong>permanent data loss</strong>. 
              This action cannot be undone. Please read all warnings carefully before proceeding.
            </AlertDescription>
          </Alert>

          <Card className="shadow-lg">
            <CardHeader className="border-b bg-amber-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Select New Start Month</CardTitle>
                  <CardDescription>
                    Choose which month your accounting records should begin from
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-6 space-y-6">
              {/* Current vs New Date Comparison */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-100 rounded-lg border border-slate-200">
                  <Label className="text-xs text-slate-500 uppercase">Current Start Month</Label>
                  <p className="text-lg font-mono font-bold text-slate-700 mt-1">
                    {formatMonthYear(business?.accounting_start_date)}
                  </p>
                </div>
                <div className="p-4 bg-[#108981]/10 rounded-lg border border-[#108981]/30">
                  <Label className="text-xs text-[#108981] uppercase">New Start Month</Label>
                  <p className="text-lg font-mono font-bold text-[#108981] mt-1">
                    {startDate ? formatMonthYear(startDate) : '(select)'}
                  </p>
                </div>
              </div>

              {/* Quick Select Options */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Quick Select</Label>
                <div className="grid grid-cols-2 gap-3">
                  {quickDateOptions.map((option, index) => (
                    <button
                      key={`${option.label}-${index}`}
                      type="button"
                      onClick={() => setStartDate(option.date)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        startDate === option.date
                          ? 'border-[#108981] bg-[#108981]/5 ring-2 ring-[#108981]/20'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Calendar className={`w-4 h-4 ${startDate === option.date ? 'text-[#108981]' : 'text-slate-400'}`} />
                        <span className={`font-medium text-sm ${startDate === option.date ? 'text-[#108981]' : 'text-slate-700'}`}>
                          {option.label}
                        </span>
                      </div>
                      <p className="text-xs font-mono text-slate-400 mt-1 ml-6">{formatMonthYear(option.date)}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Custom Date Input */}
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Or pick a specific month:</Label>
                <Input
                  type="month"
                  value={startDate ? startDate.substring(0, 7) : ''}
                  onChange={(e) => setStartDate(e.target.value ? e.target.value + '-01' : '')}
                  min="1990-01"
                  max={format(today, 'yyyy-MM')}
                  className="max-w-xs"
                  data-testid="new-start-date-input"
                />
              </div>

              {/* Preview of Changes */}
              {loadingPreview && (
                <div className="p-6 bg-slate-50 rounded-lg border flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400 mr-2" />
                  <span className="text-slate-600">Calculating impact...</span>
                </div>
              )}

              {preview && !loadingPreview && (
                <div className={`p-4 rounded-lg border ${
                  preview.warning_level === 'critical' 
                    ? 'bg-red-50 border-red-200' 
                    : preview.warning_level === 'warning'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-start gap-3">
                    {preview.direction === 'forward' ? (
                      <ArrowUp className={`w-5 h-5 flex-shrink-0 ${preview.warning_level === 'critical' ? 'text-red-600' : 'text-amber-600'}`} />
                    ) : (
                      <ArrowDown className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <h4 className={`font-semibold mb-2 ${
                        preview.warning_level === 'critical' ? 'text-red-900' : 
                        preview.warning_level === 'warning' ? 'text-amber-900' : 'text-blue-900'
                      }`}>
                        {preview.direction === 'forward' 
                          ? 'Moving Start Date FORWARD'
                          : 'Extending Your Accounting Period'}
                      </h4>
                      
                      {/* Forward direction - show deletion warning */}
                      {preview.direction === 'forward' && preview.forward_blocked && (
                        <p className="text-sm mb-3 text-red-800" data-testid="forward-move-blocked">
                          {preview.forward_block_reason} You can still move the start date <strong>backwards</strong>.
                        </p>
                      )}
                      {preview.direction === 'forward' && !preview.forward_blocked && (
                        <p className="text-sm mb-3 text-amber-800">Your books are still empty, so the start date can move forward freely.</p>
                      )}
                      
                      {/* Backward direction - Clear, organized explanation */}
                      {preview.direction === 'backward' && (
                        <div className="space-y-3">
                          {/* Main explanation */}
                          <p className="text-sm text-blue-800">
                            Your records will now start from <strong>{preview.proposed_start_date}</strong> instead of {preview.current_start_date}.
                            This extends your accounting period by <strong>{Math.abs(new Date(preview.current_start_date) - new Date(preview.proposed_start_date)) / (1000 * 60 * 60 * 24)} days</strong>.
                          </p>
                          
                          {/* What happens to your bank accounts - Clear breakdown */}
                          <div className="bg-white/60 rounded-lg p-3 space-y-2">
                            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">How older transactions will be captured:</p>
                            
                            {/* Connected Banks - Automatic */}
                            {preview.has_connected_banks && (
                              <div className="flex items-start gap-2 p-2 bg-green-50 rounded border border-green-200">
                                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-medium text-green-800">
                                    {preview.connected_bank_count} Connected Bank{preview.connected_bank_count > 1 ? 's' : ''}. Automatic
                                  </p>
                                  <p className="text-xs text-green-700">
                                    Transactions will be fetched automatically from your new start date (up to 24 months of history, depending on your bank). No action needed.
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {/* Manual Upload Accounts - Action Required */}
                            {preview.manual_account_count > 0 && (
                              <div className="flex items-start gap-2 p-2 bg-amber-50 rounded border border-amber-200">
                                <Upload className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-medium text-amber-800">
                                    {preview.manual_account_count} Manual Account{preview.manual_account_count > 1 ? 's' : ''}. Action Required
                                  </p>
                                  <p className="text-xs text-amber-700">
                                    Upload bank statements covering {preview.proposed_start_date} to {preview.current_start_date}
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {/* No accounts at all */}
                            {!preview.has_connected_banks && preview.manual_account_count === 0 && (
                              <div className="flex items-start gap-2 p-2 bg-slate-50 rounded border border-slate-200">
                                <Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm text-slate-700">
                                    No bank accounts connected yet. Add accounts after changing the date.
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Affected Counts - Only for forward direction */}
                      {preview.direction === 'forward' && !preview.forward_blocked && preview.total_affected > 0 && (
                        <div className="bg-white/60 rounded-lg p-3 mb-3">
                          <p className="text-sm font-medium text-red-900 mb-2">
                            The following will be <strong>PERMANENTLY DELETED</strong>:
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {preview.affected_counts.bank_feed_transactions > 0 && (
                              <div className="flex items-center gap-2 text-red-700">
                                <Trash2 className="w-3 h-3" />
                                <span>{preview.affected_counts.bank_feed_transactions} bank transactions</span>
                              </div>
                            )}
                            {preview.affected_counts.journals > 0 && (
                              <div className="flex items-center gap-2 text-red-700">
                                <Trash2 className="w-3 h-3" />
                                <span>{preview.affected_counts.journals} journal entries</span>
                              </div>
                            )}
                            {preview.affected_counts.bills > 0 && (
                              <div className="flex items-center gap-2 text-red-700">
                                <Trash2 className="w-3 h-3" />
                                <span>{preview.affected_counts.bills} bills</span>
                              </div>
                            )}
                            {preview.affected_counts.invoices > 0 && (
                              <div className="flex items-center gap-2 text-red-700">
                                <Trash2 className="w-3 h-3" />
                                <span>{preview.affected_counts.invoices} invoices</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Opening Balance Warning */}
                      {preview.has_opening_balances && preview.direction === 'forward' && (
                        <div className="bg-amber-100 rounded-lg p-3 mb-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-amber-900">
                                Opening Balances Must Be Deleted First
                              </p>
                              <p className="text-xs text-amber-800 mt-1">
                                You have {preview.affected_counts.opening_balances} opening balance entries. 
                                These must be deleted before changing the start date.
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-2 border-amber-400 text-amber-800"
                                onClick={() => navigate('/data-migration')}
                              >
                                Go to Opening Balances
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* New Opening Balance Date - For both directions */}
                      <div className="bg-white/60 rounded-lg p-3 mt-3">
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-4 h-4 text-slate-500" />
                          <p className="text-sm">
                            <span className="text-slate-600">New opening balance date: </span>
                            <span className="font-mono font-bold text-slate-900">{preview.proposed_opening_balance_date}</span>
                          </p>
                        </div>
                        <p className="text-xs text-slate-500 mt-1 ml-6">
                          You'll need to set opening balances as of this date after the change.
                        </p>
                      </div>

                      {/* Backlog handling choice - when the new date pulls in
                          history from before the registration month */}
                      {preview.creates_backlog && (
                        <div className="bg-white rounded-lg p-3 mt-3 border border-slate-200 space-y-2" data-testid="backlog-choice-panel">
                          <div className="flex items-start gap-2">
                            <Clock className="w-4 h-4 text-[#283F8B] mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-[#283F8B]">
                                This date brings in older history - how should we process it?
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                Applies to transactions dated before {preview.backlog_cutoff}. Newer transactions are always processed free with full AI.
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setBacklogChoice('free')}
                            data-testid="backlog-choice-free"
                            className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${backlogChoice === 'free' ? 'border-[#283F8B] bg-[#283F8B]/5' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                          >
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <span className="text-sm font-semibold text-slate-900">Free backlog</span>
                              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">No fee</span>
                            </div>
                            <p className="text-xs text-slate-600">
                              Smart-rule matching using your vendor history and our global vendor database - completely free. Anything unmatched waits in <strong>Review Needed</strong> for you to categorize.
                            </p>
                          </button>
                          {!isIOSNative() && (
                            <button
                              type="button"
                              onClick={() => setBacklogChoice('paid')}
                              data-testid="backlog-choice-paid"
                              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${backlogChoice === 'paid' ? 'border-[#283F8B] bg-[#283F8B]/5' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                            >
                              <div className="flex items-center justify-between gap-2 mb-0.5">
                                <span className="text-sm font-semibold text-slate-900">Paid backlog (AI-supported)</span>
                                <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#283F8B]/10 text-[#283F8B]">
                                  {preview.backlog_rate ? `from ${preview.backlog_rate.currency} ${preview.backlog_rate.per_transaction}/tx` : 'per-tx fee'}
                                </span>
                              </div>
                              <p className="text-xs text-slate-600">
                                AI categorizes your history and creates the matching documents. Most useful when you've been using clear, proper descriptions on your bank transactions for both <strong>inflows and outflows</strong> - that's what the AI reads to categorize accurately. Your lifetime free allowance is deducted first. You'll confirm the exact fee before anything is charged.
                              </p>
                            </button>
                          )}
                          <p className="text-xs text-slate-500">You can change this any time from Settings → General → Backlog processing.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
            
            <CardFooter className="border-t bg-slate-50 p-4">
              <div className="w-full flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowChangeMode(false);
                    setPreview(null);
                    setStartDate('');
                    setConfirmDeletion(false);
                    setConfirmFetchNeeded(false);
                    setBacklogChoice(null);
                  }}
                  className="sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleChangeSubmit}
                  disabled={!startDate || startDate === business?.accounting_start_date || loading || loadingPreview || preview?.forward_blocked || (preview?.direction === 'forward' && preview?.has_opening_balances && !confirmOpeningBalanceDeletion) || (preview?.direction === 'forward' && preview?.creates_backlog && !isIOSNative() && !backlogChoice)}
                  className={`flex-1 ${preview?.warning_level === 'critical' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#108981] hover:bg-[#108981]/90'}`}
                  data-testid="confirm-change-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : preview?.warning_level === 'critical' ? (
                    <>
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Confirm Delete & Change
                    </>
                  ) : (
                    <>
                      Change Start Date
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>

        {/* Forward Warning Dialog - Data Deletion */}
        <Dialog open={showForwardWarning} onOpenChange={setShowForwardWarning}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-full">
                  <AlertOctagon className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <DialogTitle className="text-red-900">Confirm Data Deletion</DialogTitle>
                  <DialogDescription className="text-red-700">
                    This action will permanently delete data
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            
            <div className="space-y-4">
              <Alert className="border-red-200 bg-red-50">
                <Trash2 className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  <strong>{preview?.total_affected || 0} transactions</strong> between{' '}
                  <strong>{preview?.current_start_date}</strong> and{' '}
                  <strong>{preview?.proposed_start_date}</strong> will be{' '}
                  <strong>PERMANENTLY DELETED</strong>.
                </AlertDescription>
              </Alert>

              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-slate-900 mb-2">This includes:</p>
                <ul className="text-sm text-slate-700 space-y-1">
                  {preview?.affected_counts?.bank_feed_transactions > 0 && (
                    <li>• {preview.affected_counts.bank_feed_transactions} bank feed transactions</li>
                  )}
                  {preview?.affected_counts?.journals > 0 && (
                    <li>• {preview.affected_counts.journals} journal entries</li>
                  )}
                  {preview?.affected_counts?.bills > 0 && (
                    <li>• {preview.affected_counts.bills} bills</li>
                  )}
                  {preview?.affected_counts?.invoices > 0 && (
                    <li>• {preview.affected_counts.invoices} invoices</li>
                  )}
                  {preview?.affected_counts?.other_receipts > 0 && (
                    <li>• {preview.affected_counts.other_receipts} other receipts</li>
                  )}
                  {preview?.affected_counts?.other_payments > 0 && (
                    <li>• {preview.affected_counts.other_payments} other payments</li>
                  )}
                  {preview?.affected_counts?.fixed_assets > 0 && (
                    <li>• {preview.affected_counts.fixed_assets} fixed assets and depreciation schedules</li>
                  )}
                  {preview?.affected_counts?.loans > 0 && (
                    <li>• {preview.affected_counts.loans} loans and repayment schedules</li>
                  )}
                  {preview?.affected_counts?.investments > 0 && (
                    <li>• {preview.affected_counts.investments} investments</li>
                  )}
                  {preview?.affected_counts?.opening_balances > 0 && (
                    <li>• {preview.affected_counts.opening_balances} opening balance entries (must be re-uploaded)</li>
                  )}
                  <li>• All related schedules, depreciation, and recognition entries</li>
                </ul>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <Checkbox
                  id="confirm-deletion"
                  checked={confirmDeletion}
                  onCheckedChange={setConfirmDeletion}
                  className="mt-0.5 border-red-400"
                  data-testid="confirm-deletion-checkbox"
                />
                <label htmlFor="confirm-deletion" className="text-sm text-red-800 cursor-pointer">
                  I understand that <strong>{preview?.total_affected || 0} transactions</strong> will be 
                  permanently deleted and this <strong>cannot be undone</strong>.
                </label>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowForwardWarning(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-red-600 hover:bg-red-700"
                onClick={() => {
                  setShowForwardWarning(false);
                  handleChangeSubmit();
                }}
                disabled={!confirmDeletion}
                data-testid="final-confirm-deletion-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete & Change Date
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Backward Warning Dialog - Re-fetch Needed */}
        <Dialog open={showBackwardWarning} onOpenChange={setShowBackwardWarning}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${preview?.has_connected_banks && !preview?.manual_account_count ? 'bg-green-100' : 'bg-blue-100'}`}>
                  {preview?.has_connected_banks && !preview?.manual_account_count ? (
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  ) : (
                    <Calendar className="w-6 h-6 text-blue-600" />
                  )}
                </div>
                <div>
                  <DialogTitle className={preview?.has_connected_banks && !preview?.manual_account_count ? 'text-green-900' : 'text-blue-900'}>
                    Extend Accounting Period
                  </DialogTitle>
                  <DialogDescription className="text-slate-600">
                    Capture {differenceInDays(new Date(preview?.current_start_date || new Date()), new Date(preview?.proposed_start_date || new Date()))} more days of transactions
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Period Change Summary */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Current Start</p>
                  <p className="font-mono font-bold text-slate-700">{preview?.current_start_date}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">New Start</p>
                  <p className="font-mono font-bold text-[#108981]">{preview?.proposed_start_date}</p>
                </div>
              </div>

              {/* Start-date rules v2 · the SAME impact review as the upload-driven move-back (banks needing statements,
                  opening balances + follow-on entries to be removed, matched rows) with its three confirmations. */}
              {preview?.impact ? (
                <StartDateImpactReview impact={preview.impact} onChange={(st) => { setConfirmFetchNeeded(st.confirmed); if (st.backlog) setBacklogChoice(st.backlog); }} />
              ) : (
                <p className="text-sm text-slate-600">Loading impact review…</p>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBackwardWarning(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  setShowBackwardWarning(false);
                  handleChangeSubmit();
                }}
                disabled={!confirmFetchNeeded || !preview?.impact}
                data-testid="final-confirm-backward-btn"
              >
                <ArrowDown className="w-4 h-4 mr-2" />
                Move start date back
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  
  // Render initial setup interface (first time setting the date)
  return (
    <div className={embedded ? "" : "min-h-dvh bg-gradient-to-b from-slate-50 to-slate-100 p-4"}>
      <div className={embedded ? "" : "max-w-2xl mx-auto pt-8"}>
        
        {/* Header */}
        {!embedded && (
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#108981]/10 rounded-full mb-4">
              <CalendarDays className="w-8 h-8 text-[#108981]" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">When Should We Start Capturing?</h1>
            <p className="text-slate-600 mt-2">
              Choose the month from which AkountFly automatically tracks your financial activity
            </p>
          </div>
        )}
        
        {/* Main Card */}
        <Card className="shadow-lg">
          <CardHeader className="border-b bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg">When should we start capturing your finances?</CardTitle>
                <CardDescription>
                  Choose the month from which we'll automatically track your transactions, invoices, and bills
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-6 space-y-6">
            
            {/* WHY THIS MATTERS - Simple explanation */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex gap-3">
                <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-2">Why do I need to set this?</h3>
                  <p className="text-blue-800 text-sm">
                    This is the date from which we'll <strong>automatically capture</strong> all your financial activity: 
                    bank transactions, invoices, bills, and payments. Everything before this date becomes 
                    your "opening balance": a snapshot of where your business stood the day before.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Quick Select Options */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Choose a Start Month</Label>
              <p className="text-xs text-muted-foreground">Select the month from which your accounting records begin</p>
              <div className="grid grid-cols-2 gap-3">
                {quickDateOptions.map((option, index) => (
                  <button
                    key={`initial-${option.label}-${index}`}
                    type="button"
                    onClick={() => setStartDate(option.date)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      startDate === option.date
                        ? 'border-[#108981] bg-[#108981]/5 ring-2 ring-[#108981]/20'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Calendar className={`w-4 h-4 ${startDate === option.date ? 'text-[#108981]' : 'text-slate-400'}`} />
                      <span className={`font-medium text-sm ${startDate === option.date ? 'text-[#108981]' : 'text-slate-700'}`}>
                        {option.label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-6">{option.description}</p>
                    <p className="text-xs font-mono text-slate-400 mt-0.5 ml-6">{formatMonthYear(option.date)}</p>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Custom Month Input */}
            <div className="space-y-2">
              <Label className="text-sm text-slate-600">Or pick a specific month:</Label>
              <Input
                type="month"
                value={startDate ? startDate.substring(0, 7) : ''}
                onChange={(e) => setStartDate(e.target.value ? e.target.value + '-01' : '')}
                min="1990-01"
                max={format(today, 'yyyy-MM')}
                className="max-w-xs"
                data-testid="accounting-start-date-input"
              />
            </div>
            
            {/* What This Means - Visual breakdown */}
            {startDate && (
              <div className="p-4 bg-slate-50 rounded-lg border space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Info className="w-4 h-4 text-[#108981]" />
                  Here's what happens:
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Opening Balance Date */}
                  <div className="p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-amber-100 rounded">
                        <BookOpen className="w-4 h-4 text-amber-600" />
                      </div>
                      <span className="font-medium text-sm">Opening Balance Date</span>
                    </div>
                    <p className="text-lg font-mono font-bold text-amber-600">{formatMonthYear(openingBalanceDate)}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Your financial starting point: enter what you owned & owed as of this date
                    </p>
                  </div>
                  
                  {/* Transactions Start */}
                  <div className="p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-green-100 rounded">
                        <CircleDollarSign className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="font-medium text-sm">Auto-Capture Begins</span>
                    </div>
                    <p className="text-lg font-mono font-bold text-green-600">{formatMonthYear(startDate)}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      We automatically track all transactions, invoices, and bills from this date
                    </p>
                  </div>
                </div>
                
                {/* Bank Feed Warning */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Bank transactions from this date</p>
                      <ul className="text-xs text-amber-700 mt-1 space-y-0.5">
                        <li>Your <strong>bank balance and all transactions</strong> from {formatMonthYear(startDate)} will be pulled automatically via bank sync or uploaded statements</li>
                        <li>If your bank transactions don't have clear descriptions, some will be flagged as <strong>"Needs Review"</strong> in your Bank Feed</li>
                        <li>The balance at end of <strong>{formatMonthYear(openingBalanceDate)}</strong> becomes your opening balance</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Rules */}
                <div className="space-y-2 pt-2">
                  <div className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Bank feeds will automatically pull transactions from <strong>{formatMonthYear(startDate)}</strong> onwards</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Invoices, bills, and manual entries can only be created for <strong>{formatMonthYear(startDate)}</strong> or later</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <Lock className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span className="text-amber-700">This date can be changed later, but may affect existing records</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Confirmation Checkbox */}
            <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
              <Checkbox
                id="confirm"
                checked={confirmUnderstanding}
                onCheckedChange={setConfirmUnderstanding}
                className="mt-0.5"
                data-testid="confirm-understanding-checkbox"
              />
              <label htmlFor="confirm" className="text-sm text-slate-700 cursor-pointer">
                I understand that AkountFly will automatically capture my financial data from <strong>{formatMonthYear(startDate) || '(select a month)'}</strong>, 
                and my opening balances will be set as of the end of the month before.
              </label>
            </div>
            
          </CardContent>
          
          <CardFooter className="border-t bg-slate-50 p-4">
            <div className="w-full flex flex-col sm:flex-row gap-3">
              {!embedded && (
                <Button
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="sm:w-auto"
                >
                  Back
                </Button>
              )}
              <Button
                onClick={handleInitialSubmit}
                disabled={!startDate || !confirmUnderstanding || loading}
                className="flex-1 bg-[#108981] hover:bg-[#108981]/90"
                data-testid="activate-platform-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Activate Platform
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardFooter>
        </Card>
        
        {/* Help Section */}
        <div className="mt-6 p-4 bg-white rounded-lg border shadow-sm">
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#108981]" />
            Not sure which month to choose?
          </h3>
          <div className="space-y-2 text-sm text-slate-600">
            <p>
              <strong>Just starting your business?</strong> Use the current month: it's free.
            </p>
            <p>
              <strong>Been in business for a while?</strong> Use the start of your current financial year 
              so you have a full year of records. A small backlog fee applies for prior months.
            </p>
            <p>
              <strong>Switching from another system?</strong> Use the month when you stopped using the old 
              system, and enter opening balances to bring forward your previous records.
            </p>
          </div>
        </div>
        
      </div>

      {/* Backlog Fee Dialog - shown when user picks a past start date */}
      <BacklogFeeDialog
        open={showBacklogFee}
        onOpenChange={setShowBacklogFee}
        feeInfo={backlogFeeInfo}
        onConfirm={() => {
          setShowBacklogFee(false);
          doActivatePlatform();
        }}
        onCancel={() => {
          setShowBacklogFee(false);
          setStartDate(startOfCurrentMonth);
          toast.info('Start month set to current month: no backlog fee required.');
        }}
      />
    </div>
  );
};

export default AccountingStartDateSetup;
